// V1 source: customer/midas/components/MidasArtifactHeader.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 dock header — tab strip + (i) describe-page button + dock-close (X).
// Logic copied verbatim from V1: resolveInfoPrompt, synthesiseInfoPrompt,
// resolveLocalisedName, buildFriendlyDisplayText, isStreamingGlobal selector,
// MC-26/MC-27 info-prompt dispatch (sendMessage with displayText +
// pageInfoRequest).
//
// Visual layer rewritten in Tailwind. Lucide-react icons replace MUI icons.
// Per Paper artboard `MIDAS V2 — Phase 2 — Dock — Spec` (panels A, B, C, K):
//   - Multi-tab strip: 5 visible (most-recent first); +N chevron dropdown
//     for overflow; active = bone fill + 2px terracotta bottom border + 10px
//     square chip. Inactive = transparent, muted graphite, stone hairline
//     separators. Long titles ellipsis-truncate at ~24 chars (max-width
//     ~120px). Per-tab × close (14–16px hit, warm-graphite stroke).
//   - Utility row beneath tabs: JBM 10px uppercase eyebrow ("ARTIFACT DOCK ·
//     DIAGRAM · 3 OPEN") + Sora 14/600 title + " · sheet 3 of 7" mono suffix.
//     Right cluster: (i) describe + dock-level ×.
//   - (i) Describe-page button: 24×24 rounded pill, 1px terracotta border,
//     italic terracotta glyph on rgba(212,87,43,0.08) ground.
//   - Dock-level close (K): visually distinct from per-tab × — heavier
//     graphite stroke, no rounded chip background. Wires to existing
//     closeArtifactPanel slice action.
//
// Visible-strip cap = VISIBLE_TABS_IN_STRIP = 5 (slice constant, no eviction).
// Older tabs accumulate in the +N dropdown — the slice keeps the full list.

import React from "react";
import { Info, X, ChevronDown } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import i18n from "../../../../core/i18n";
import { sendMessage } from "../../../../shared/chatBot/chatBotSlice";
import { DiagramOverlayArtifactData } from "../../../../shared/chatBot/models/chatBotModels";
import {
  closeArtifactPanel,
  closeArtifactTab,
  selectOverflowArtifactTabs,
  selectVisibleArtifactTabs,
  setActiveArtifactTab,
} from "../../midasPageSlice";
import { ArtifactTab } from "../../midasPageModels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

// ===================================================================
// Title truncation — verbatim length from V1
// ===================================================================

const MAX_TAB_TITLE_LENGTH = 24;
const truncateTitle = (title: string): string => {
  if (title.length <= MAX_TAB_TITLE_LENGTH) return title;
  return title.slice(0, MAX_TAB_TITLE_LENGTH - 1) + "…";
};

// ===================================================================
// i18n helpers — verbatim from V1 MidasArtifactHeader
// ===================================================================

/** MC-26: resolve localised info_prompt body with fallback chain
 * user language → sk → en → first available key */
const resolveInfoPrompt = (
  infoPrompt: { sk?: string; en?: string; cs?: string } | undefined,
  lang: string
): string | null => {
  if (!infoPrompt) return null;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  const primary = (infoPrompt as any)[langKey];
  if (primary && typeof primary === "string" && primary.length > 0) return primary;
  if (infoPrompt.sk && infoPrompt.sk.length > 0) return infoPrompt.sk;
  if (infoPrompt.en && infoPrompt.en.length > 0) return infoPrompt.en;
  if (infoPrompt.cs && infoPrompt.cs.length > 0) return infoPrompt.cs;
  for (const v of Object.values(infoPrompt)) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
};

/** MC-26: localised name fallback (user lang → sk → en → cs → first available). */
const resolveLocalisedName = (
  data: DiagramOverlayArtifactData | undefined,
  lang: string
): string | undefined => {
  if (!data) return undefined;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en" && data.name_en) return data.name_en;
  if (langKey === "cs" && data.name_cs) return data.name_cs;
  if (data.name) return data.name;
  if (data.name_en) return data.name_en;
  if (data.name_cs) return data.name_cs;
  if (data.page_name) return data.page_name;
  return undefined;
};

/** MC-26 fallback: synthesise an info-prompt for diagram_overlay artifacts
 * the bot hasn't enriched. Real `info_prompt` (when present) wins. */
const synthesiseInfoPrompt = (tab: ArtifactTab, lang: string): string | null => {
  if (tab.artifact.type !== "diagram_overlay") return null;
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  if (page === undefined || page === null) return null;
  const name = resolveLocalisedName(data, lang) ?? tab.artifact.title;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name
      ? `Explain page ${page} — ${name} from the electrical schema collection`
      : `Explain page ${page} from the electrical schema collection`;
  }
  if (langKey === "cs") {
    return name
      ? `Vysvětli mi stránku ${page} — ${name} ze sbírky elektrických schémat`
      : `Vysvětli mi stránku ${page} ze sbírky elektrických schémat`;
  }
  return name
    ? `Vysvetli mi stránku ${page} — ${name} zo zbierky elektrických schém`
    : `Vysvetli mi stránku ${page} zo zbierky elektrických schém`;
};

/** MC-26: friendly display text for the chat bubble. */
const buildFriendlyDisplayText = (tab: ArtifactTab, lang: string): string => {
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  const name = resolveLocalisedName(data, lang);
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name ? `Describe page ${page} — ${name}` : `Describe page ${page}`;
  }
  if (langKey === "cs") {
    return name ? `Popis stránky ${page} — ${name}` : `Popis stránky ${page}`;
  }
  return name ? `Popis stránky ${page} — ${name}` : `Popis stránky ${page}`;
};

// ===================================================================
// TabPill — single tab in the strip
// ===================================================================

interface TabPillProps {
  tab: ArtifactTab;
  isActive: boolean;
  onActivate: () => void;
  onClose: (e: React.MouseEvent) => void;
}

const TabPill: React.FC<TabPillProps> = ({
  tab,
  isActive,
  onActivate,
  onClose,
}) => {
  const titleClass = [
    "truncate font-mono text-[11px] tracking-[0.04em]",
    isActive
      ? "font-semibold text-midas-graphite"
      : "font-normal text-midas-warm-graphite",
  ].join(" ");

  return (
    <div
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      className={[
        "group relative flex min-w-0 max-w-[180px] flex-shrink cursor-pointer items-center gap-1 px-2.5 py-1 transition-colors duration-150",
        isActive
          ? "bg-midas-bone"
          : "border-r border-midas-stone/60 hover:bg-midas-warm-stone/40",
      ].join(" ")}
      style={
        isActive
          ? { boxShadow: "inset 0 -2px 0 0 #D4572B" }
          : undefined
      }
    >
      {/* 10px square chip on active — terracotta */}
      {isActive && (
        <span
          aria-hidden="true"
          className="h-[10px] w-[10px] flex-shrink-0 rounded-[2px] bg-midas-terracotta"
        />
      )}
      <span className={titleClass}>{truncateTitle(tab.artifact.title)}</span>
      <button
        type="button"
        aria-label="Close tab"
        onClick={onClose}
        className="ml-1 inline-flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center rounded-full text-midas-warm-graphite transition-colors hover:bg-midas-stone/60 hover:text-midas-graphite"
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </div>
  );
};

// ===================================================================
// MidasArtifactHeaderV2
// ===================================================================

const MidasArtifactHeaderV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const visibleTabs = useAppSelector(selectVisibleArtifactTabs);
  const overflowTabs = useAppSelector(selectOverflowArtifactTabs);
  const allTabs = useAppSelector((state) => state.midasPage.artifactTabs);
  const activeTabId = useAppSelector((state) => state.midasPage.activeArtifactTabId);
  const activeThreadId = useAppSelector((state) => state.midasPage.activeThreadId);
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);

  // MC-26: disable (i) buttons while any stream is active (per Q7 — global debounce)
  const isStreamingGlobal = useAppSelector((state) => {
    if (state.chatBot.isLoading) return true;
    const buffers = state.chatBot.threadBuffers || {};
    for (const key of Object.keys(buffers)) {
      if (buffers[key]?.isLoading) return true;
    }
    return false;
  });

  const activeTab = allTabs.find((t) => t.id === activeTabId);
  const overflowCount = overflowTabs.length;

  // MC-26: compute info_prompt availability for the active tab.
  const infoPromptBody = React.useMemo(() => {
    if (!activeTab) return null;
    const data = activeTab.artifact.data as DiagramOverlayArtifactData | undefined;
    const fromBot = resolveInfoPrompt(data?.info_prompt, i18n.language);
    if (fromBot) return fromBot;
    return synthesiseInfoPrompt(activeTab, i18n.language);
  }, [activeTab]);

  const handleActivate = (tabId: string) => {
    dispatch(setActiveArtifactTab(tabId));
  };

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    dispatch(closeArtifactTab(tabId));
  };

  const handleClosePanel = () => {
    dispatch(closeArtifactPanel());
  };

  const handleInfoClick = React.useCallback(() => {
    if (!activeTab) return;
    if (isStreamingGlobal) return; // belt-and-braces; button is also disabled
    const data = activeTab.artifact.data as DiagramOverlayArtifactData | undefined;
    const fromBot = resolveInfoPrompt(data?.info_prompt, i18n.language);
    const promptBody = fromBot ?? synthesiseInfoPrompt(activeTab, i18n.language);
    if (!promptBody) return;
    const friendly = buildFriendlyDisplayText(activeTab, i18n.language);

    // MC-27: structured page hint for diagram_overlay artifacts.
    let pageInfoRequest:
      | { page: number; collection: string; list_id?: string }
      | undefined;
    if (activeTab.artifact.type === "diagram_overlay") {
      const page = data?.page ?? activeTab.artifact.page;
      if (typeof page === "number" && Number.isFinite(page)) {
        pageInfoRequest = {
          page,
          collection: data?.collection ?? "sbl500_electrical_toc_v2",
          ...(data?.list_id ? { list_id: data.list_id } : {}),
        };
      }
    }

    dispatch(
      sendMessage({
        message: promptBody,
        displayText: friendly,
        machineId: machineContext?.machineId,
        threadId: activeThreadId || undefined,
        originThreadId: activeThreadId || undefined,
        pageInfoRequest,
      })
    );
  }, [activeTab, isStreamingGlobal, activeThreadId, machineContext, dispatch]);

  // Eyebrow: "ARTIFACT DOCK · IMAGE · 3 OPEN" — type comes from active tab
  const activeType = activeTab?.artifact.type ?? "image";
  const eyebrowType = (activeType === "diagram_overlay" ? "diagram" : activeType)
    .toString()
    .replace(/_/g, " ")
    .toUpperCase();
  const totalOpen = allTabs.length;

  return (
    <header className="flex-shrink-0 border-b border-midas-stone bg-midas-bone-light">
      {/* Tab strip — visible 5 + +N dropdown */}
      <div
        role="tablist"
        aria-label="Artifact tabs"
        className="flex h-[36px] items-stretch overflow-hidden border-b border-midas-stone/40"
      >
        <div className="flex flex-1 items-stretch overflow-hidden">
          {visibleTabs.map((tab) => (
            <TabPill
              key={tab.id}
              tab={tab}
              isActive={tab.id === activeTabId}
              onActivate={() => handleActivate(tab.id)}
              onClose={(e) => handleCloseTab(e, tab.id)}
            />
          ))}
        </div>

        {/* +N overflow dropdown */}
        {overflowCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Show ${overflowCount} additional artifacts`}
                className="inline-flex flex-shrink-0 items-center gap-1 border-l border-midas-stone bg-midas-warm-stone/40 px-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.04em] text-midas-graphite transition-colors hover:bg-midas-warm-stone"
              >
                <span>+{overflowCount}</span>
                <ChevronDown size={12} strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[280px] border-midas-stone bg-midas-bone-light p-1 font-sora text-[12px] text-midas-graphite shadow-lg"
            >
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
                {`Open Artifacts · ${visibleTabs.length} in strip · ${overflowCount} overflow`}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-midas-stone" />
              {/* Visible strip — listed for completeness so users see the
                  full set in one place */}
              {visibleTabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.id}
                  onSelect={() => handleActivate(tab.id)}
                  className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-[12px] data-[highlighted]:bg-midas-warm-stone data-[highlighted]:text-midas-graphite"
                >
                  <span className="truncate">
                    {tab.artifact.title}
                  </span>
                  {tab.id === activeTabId && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-midas-terracotta">
                      Active
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-midas-stone" />
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
                Off-strip · accessible via dropdown
              </DropdownMenuLabel>
              {overflowTabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.id}
                  onSelect={() => handleActivate(tab.id)}
                  className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-[12px] data-[highlighted]:bg-midas-warm-stone data-[highlighted]:text-midas-graphite"
                >
                  <span className="truncate">{tab.artifact.title}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      dispatch(closeArtifactTab(tab.id));
                    }}
                    aria-label="Close tab"
                    className="inline-flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center rounded-full text-midas-muted-stone transition-colors hover:bg-midas-stone hover:text-midas-graphite"
                  >
                    <X size={11} strokeWidth={2.4} />
                  </button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Utility row — eyebrow + title + (i) + dock-close */}
      <div className="flex items-start gap-3 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-midas-muted-stone">
            {`Artifact Dock · ${eyebrowType} · ${totalOpen} Open`}
          </div>
          {activeTab && (
            <div
              className="truncate font-sora text-[14px] font-semibold text-midas-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {activeTab.artifact.title}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 pt-0.5">
          {/* (i) Describe-page button — visible only when active artifact has info_prompt */}
          {infoPromptBody && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleInfoClick}
                  disabled={isStreamingGlobal}
                  aria-label={translate("midas.artifact.describePageAria")}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-midas-terracotta text-midas-terracotta transition-colors hover:bg-midas-terracotta-soft disabled:cursor-not-allowed disabled:border-midas-stone disabled:bg-transparent disabled:text-midas-muted-stone disabled:opacity-60"
                  style={{
                    backgroundColor: isStreamingGlobal
                      ? "transparent"
                      : "rgba(212, 87, 43, 0.08)",
                  }}
                >
                  <Info size={13} strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
              >
                {translate("midas.artifact.describePage")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Dock-level close (K) — heavier graphite stroke, no chip background */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleClosePanel}
                aria-label={translate("close")}
                className="inline-flex h-6 w-6 items-center justify-center text-midas-graphite transition-colors hover:text-midas-terracotta"
              >
                <X size={18} strokeWidth={2.4} />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="border-midas-stone bg-midas-graphite font-sora text-[11px] text-midas-bone"
            >
              {translate("close")}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </header>
  );
};

export default MidasArtifactHeaderV2;
